import { Bandeira, type Settings, type UpdateSettingsBody } from '@deizi/shared';
import { useState } from 'react';
import { AffixInput, Field, SaveBar, Seg } from '../../../components/Form';
import { Alert } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { errorMessage } from '../../../lib/messages';
import {
  bpsInput,
  centavosInput,
  decimalInput,
  parseBps,
  parseCentavos,
  parseDecimal,
} from '../../../lib/numbers';
import { useSettingsDefaults, useUpdateSettings } from '../../../lib/settings-api';
import { BANDEIRA_LABELS } from './format';
import { HistoryPanel } from './HistoryPanel';

type Costs = Settings['costs'];
type Kind = 'kwh' | 'money' | 'bps';
type NumericKey = Exclude<keyof Costs, 'bandeira' | 'taxMode'>;

const KIND: Record<NumericKey, Kind> = {
  energyTariffPerKwh: 'kwh',
  bandeiraPerKwh: 'kwh',
  laborRatePerHourCentavos: 'money',
  designRatePerHourCentavos: 'money',
  failureBps: 'bps',
  wasteBps: 'bps',
  targetMarginBps: 'bps',
  minimumMarginBps: 'bps',
  dasMonthlyCentavos: 'money',
  taxBps: 'bps',
};

const NUMERIC_KEYS: NumericKey[] = [
  'energyTariffPerKwh',
  'bandeiraPerKwh',
  'laborRatePerHourCentavos',
  'designRatePerHourCentavos',
  'failureBps',
  'wasteBps',
  'targetMarginBps',
  'minimumMarginBps',
  'dasMonthlyCentavos',
  'taxBps',
];

const toText = (key: NumericKey, v: number | null): string =>
  KIND[key] === 'kwh'
    ? decimalInput(v, 5, 2)
    : KIND[key] === 'money'
      ? centavosInput(v)
      : bpsInput(v);
const fromText = (key: NumericKey, t: string): number | null =>
  KIND[key] === 'kwh' ? parseDecimal(t) : KIND[key] === 'money' ? parseCentavos(t) : parseBps(t);

type Form = Record<NumericKey, string> & { bandeira: Costs['bandeira']; taxMode: Costs['taxMode'] };

function toForm(c: Costs): Form {
  const t = (k: NumericKey) => toText(k, c[k]);
  return {
    energyTariffPerKwh: t('energyTariffPerKwh'),
    bandeiraPerKwh: t('bandeiraPerKwh'),
    laborRatePerHourCentavos: t('laborRatePerHourCentavos'),
    designRatePerHourCentavos: t('designRatePerHourCentavos'),
    failureBps: t('failureBps'),
    wasteBps: t('wasteBps'),
    targetMarginBps: t('targetMarginBps'),
    minimumMarginBps: t('minimumMarginBps'),
    dasMonthlyCentavos: t('dasMonthlyCentavos'),
    taxBps: t('taxBps'),
    bandeira: c.bandeira,
    taxMode: c.taxMode,
  };
}

/** Custos tab (design Config-Custos): every change needs a motivo and goes to the history. */
export function CostsTab({ settings, readOnly }: { settings: Settings; readOnly: boolean }) {
  const saved = settings.costs;
  const [form, setForm] = useState<Form>(() => toForm(saved));
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const update = useUpdateSettings();
  const defaults = useSettingsDefaults();
  const toast = useToast();

  const changes: Partial<Costs> = {};
  const invalid = new Set<NumericKey>();
  for (const key of NUMERIC_KEYS) {
    const value = fromText(key, form[key]);
    if (form[key].trim() !== '' && value === null) invalid.add(key);
    else if (value !== saved[key]) (changes as Record<string, unknown>)[key] = value;
  }
  if (form.bandeira !== saved.bandeira) changes.bandeira = form.bandeira;
  if (form.taxMode !== saved.taxMode) changes.taxMode = form.taxMode;
  const changedCount = Object.keys(changes).length;
  const isChanged = (k: keyof Costs) => k in changes;
  const was = (k: NumericKey) => (isChanged(k) && saved[k] !== null ? toText(k, saved[k]) : null);

  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const num = (
    k: NumericKey,
    props: { label: string; prefix?: string; unit?: string; placeholder?: string; help?: string },
  ) => (
    <Field
      id={`c-${k}`}
      label={props.label}
      was={was(k)}
      help={props.help}
      error={invalid.has(k) ? 'Número inválido' : null}
    >
      <AffixInput
        id={`c-${k}`}
        value={form[k]}
        onChange={(v) => set(k, v)}
        prefix={props.prefix}
        unit={props.unit}
        placeholder={props.placeholder}
        disabled={readOnly}
        changed={isChanged(k)}
        invalid={invalid.has(k)}
      />
    </Field>
  );

  const tariff = parseDecimal(form.energyTariffPerKwh) ?? 0;
  const surcharge = form.bandeira === 'verde' ? 0 : (parseDecimal(form.bandeiraPerKwh) ?? 0);
  const energyPerHour = 0.25 * (tariff + surcharge);

  const save = () => {
    if (invalid.size > 0) return;
    if (reason.trim().length < 3) {
      setReasonError(true);
      return;
    }
    const body: UpdateSettingsBody = { costs: changes, reason: reason.trim() };
    update.mutate(body, {
      onSuccess: (s) => {
        setForm(toForm(s.costs));
        setReason('');
        setReasonError(false);
        toast(changedCount === 1 ? 'Parâmetro salvo' : `${changedCount} parâmetros salvos`);
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <div className="col" style={{ flex: '3 1 520px', minWidth: 0, gap: 18 }}>
        <section className="card col" style={{ gap: 16 }}>
          <h2>Energia</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))',
              gap: 14,
            }}
          >
            {num('energyTariffPerKwh', {
              label: 'Tarifa com tributos',
              prefix: 'R$',
              unit: '/kWh',
              help: 'TE + TUSD com ICMS. Confira o “preço unitário com tributos” na conta.',
            })}
            <Field
              id="c-bandeira"
              label="Bandeira do mês"
              was={isChanged('bandeira') ? BANDEIRA_LABELS[saved.bandeira] : null}
            >
              <select
                id="c-bandeira"
                className={
                  isChanged('bandeira') ? 'input changed' : readOnly ? 'input readonly' : 'input'
                }
                value={form.bandeira}
                disabled={readOnly}
                onChange={(e) => {
                  const flag = Bandeira.options.find((f) => f === e.target.value) ?? 'verde';
                  const suggestion = defaults.data?.bandeiraSuggestions[flag] ?? null;
                  setForm((f) => ({
                    ...f,
                    bandeira: flag,
                    bandeiraPerKwh:
                      flag === 'verde'
                        ? '0,00'
                        : suggestion !== null
                          ? decimalInput(suggestion, 5, 3)
                          : '',
                  }));
                }}
              >
                {Object.entries(BANDEIRA_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            {form.bandeira !== 'verde'
              ? num('bandeiraPerKwh', {
                  label: 'Acréscimo da bandeira',
                  prefix: '+ R$',
                  unit: '/kWh',
                  placeholder: 'confira na conta',
                  help: 'Com tributos, por kWh.',
                })
              : null}
          </div>
          <p className="xs muted">
            X2D com PLA: 250 W × R$ {decimalInput(tariff + surcharge, 5, 2)} = R${' '}
            {decimalInput(energyPerHour, 2, 2)}/h de energia.
          </p>
        </section>

        <section className="card col" style={{ gap: 16 }}>
          <h2>Mão de obra</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))',
              gap: 14,
            }}
          >
            {num('laborRatePerHourCentavos', {
              label: 'Acabamento e embalagem',
              prefix: 'R$',
              unit: '/h',
            })}
            {num('designRatePerHourCentavos', {
              label: 'Projeto',
              prefix: 'R$',
              unit: '/h',
              help: 'Modelagem sob encomenda.',
            })}
          </div>
        </section>

        <section className="card col" style={{ gap: 16 }}>
          <h2>Perdas e margens</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))',
              gap: 14,
            }}
          >
            {num('failureBps', { label: 'Taxa de falha', unit: '%' })}
            {num('wasteBps', { label: 'Desperdício', unit: '%' })}
            {num('targetMarginBps', { label: 'Margem alvo', unit: '%' })}
            {num('minimumMarginBps', {
              label: 'Margem mínima',
              unit: '%',
              help: 'Abaixo disso, o preço fica vermelho.',
            })}
          </div>
          {(parseBps(form.minimumMarginBps) ?? 0) > (parseBps(form.targetMarginBps) ?? 0) ? (
            <Alert tone="warn">
              A margem mínima está maior que a margem alvo. Todo preço sugerido vai aparecer
              vermelho.
            </Alert>
          ) : null}
        </section>

        <section className="card col" style={{ gap: 14 }}>
          <h2>Imposto</h2>
          <Seg
            label="Forma do imposto"
            value={form.taxMode}
            onChange={(v) => set('taxMode', v)}
            disabled={readOnly}
            options={[
              { value: 'fixed', label: 'Valor fixo por mês (MEI)' },
              { value: 'percent', label: '% do faturamento' },
            ]}
          />
          {form.taxMode === 'percent' ? (
            <div style={{ maxWidth: 260 }}>
              {num('taxBps', {
                label: 'Alíquota sobre o faturamento',
                unit: '%',
                placeholder: 'alíquota',
                help: 'Somada às tarifas do canal em cada preço sugerido.',
              })}
            </div>
          ) : (
            <div style={{ maxWidth: 260 }}>
              {num('dasMonthlyCentavos', {
                label: 'DAS mensal',
                prefix: 'R$',
                placeholder: 'valor do DAS',
                help: 'Entra nos custos fixos do mês, não no preço de cada peça.',
              })}
            </div>
          )}
        </section>

        {changedCount > 0 && !readOnly ? (
          <>
            <section className="card col" style={{ gap: 8, borderColor: 'var(--co)' }}>
              <Field
                id="why"
                label="Motivo da mudança (obrigatório)"
                error={
                  reasonError
                    ? 'Escreva o motivo: ele fica no histórico ao lado de cada valor.'
                    : null
                }
              >
                <input
                  id="why"
                  className={reasonError ? 'input err' : 'input'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex.: reajuste da CELESC, falhas de adesão em setembro"
                />
              </Field>
            </section>
            {update.isError ? <Alert tone="err">{errorMessage(update.error)}</Alert> : null}
            <SaveBar
              label={
                changedCount === 1
                  ? '1 alteração não salva.'
                  : `${changedCount} alterações não salvas.`
              }
              detail="Vale para orçamentos novos; os já salvos guardam os valores da época."
              onDiscard={() => {
                setForm(toForm(saved));
                setReason('');
                setReasonError(false);
                toast('Alterações descartadas');
              }}
              onSave={save}
              saving={update.isPending}
            />
          </>
        ) : null}
      </div>
      <HistoryPanel filter="costs" />
    </div>
  );
}
